/**
 * Hero Audio Control Component
 * Handles audio toggle for hero section videos (native, YouTube, and Vimeo)
 * Respects responsive visibility: desktop media on desktop, mobile media on mobile
 * Ensures ONLY the correct video plays based on viewport size
 */

class HeroAudioControl {
  constructor() {
    this.videoWrappers = [];
    this.youtubeAPIReady = false;
    this.vimeoAPIReady = false;
    this.youtubePlayers = new Map();
    this.vimeoPlayers = new Map();
    this.mobileBreakpoint = 749; // Must match CSS breakpoint
    this.resizeTimeout = null;
    this.currentViewport = null; // 'desktop' or 'mobile'
    
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Find all video wrappers (both desktop and mobile)
    this.videoWrappers = document.querySelectorAll('[data-video-wrapper]');
    
    if (this.videoWrappers.length === 0) return;

    // Determine current viewport
    this.updateCurrentViewport();

    // Load YouTube API if needed
    const hasYouTube = this.checkForYouTube();
    if (hasYouTube) {
      this.loadYouTubeAPI();
    }

    // Load Vimeo API if needed
    const hasVimeo = this.checkForVimeo();
    if (hasVimeo) {
      this.loadVimeoAPI();
    }

    // Setup native video controls immediately
    this.setupNativeVideos();

    // Setup audio toggle buttons
    this.setupAudioToggles();

    // Add resize listener to handle viewport changes
    this.setupResizeListener();

    // Initial video visibility check
    this.handleVideoVisibility();
  }

  updateCurrentViewport() {
    const width = window.innerWidth;
    this.currentViewport = width <= this.mobileBreakpoint ? 'mobile' : 'desktop';
  }

  isMobile() {
    return window.innerWidth <= this.mobileBreakpoint;
  }

  isDesktop() {
    return window.innerWidth > this.mobileBreakpoint;
  }

  setupResizeListener() {
    let previousViewport = this.currentViewport;

    window.addEventListener('resize', () => {
      // Clear previous timeout
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      // Debounce resize event
      this.resizeTimeout = setTimeout(() => {
        this.updateCurrentViewport();

        // Only handle if viewport type changed (mobile <-> desktop)
        if (this.currentViewport !== previousViewport) {
          console.log(`[Hero] Viewport changed: ${previousViewport} -> ${this.currentViewport}`);
          this.handleVideoVisibility();
          previousViewport = this.currentViewport;
        }
      }, 150);
    });
  }

  handleVideoVisibility() {
    // Check if hero has custom mobile media
    const heroSection = document.querySelector('.hero--has-mobile-media');
    const hasCustomMobileMedia = !!heroSection;

    this.videoWrappers.forEach(wrapper => {
      const videoType = wrapper.getAttribute('data-video-type'); // 'desktop' or 'mobile'
      const video = wrapper.querySelector('video');
      const iframe = wrapper.querySelector('iframe');

      // Determine if this video should be active based on current viewport
      let shouldBeActive = false;

      if (this.isDesktop()) {
        // Desktop: ALWAYS play desktop videos, NEVER play mobile videos
        // This ensures desktop media plays regardless of custom mobile media settings
        shouldBeActive = (videoType === 'desktop');
      } else {
        // Mobile: Show mobile videos if custom mobile media exists, otherwise show desktop videos as fallback
        if (hasCustomMobileMedia) {
          shouldBeActive = (videoType === 'mobile');
        } else {
          shouldBeActive = (videoType === 'desktop');
        }
      }

      // Play or pause based on whether video should be active in current viewport
      if (shouldBeActive) {
        this.playVideo(wrapper, video, iframe);
      } else {
        this.pauseVideo(wrapper, video, iframe);
      }
    });
  }

  playVideo(wrapper, video, iframe) {
    // Native video
    if (video) {
      video.play().catch(err => {
        console.log('[Hero] Video autoplay prevented:', err);
        // Try to unmute and play as a workaround
        video.muted = true;
        video.play().catch(e => console.log('[Hero] Video play failed:', e));
      });
      return;
    }

    // YouTube video
    const youtubePlayer = this.youtubePlayers.get(wrapper);
    if (youtubePlayer && youtubePlayer.playVideo) {
      try {
        youtubePlayer.playVideo();
      } catch (err) {
        console.log('[Hero] YouTube play error:', err);
      }
      return;
    }

    // Vimeo video
    const vimeoPlayer = this.vimeoPlayers.get(wrapper);
    if (vimeoPlayer) {
      vimeoPlayer.play().catch(err => {
        console.log('[Hero] Vimeo play error:', err);
      });
      return;
    }
  }

  pauseVideo(wrapper, video, iframe) {
    // Native video
    if (video) {
      video.pause();
      return;
    }

    // YouTube video
    const youtubePlayer = this.youtubePlayers.get(wrapper);
    if (youtubePlayer && youtubePlayer.pauseVideo) {
      try {
        youtubePlayer.pauseVideo();
      } catch (err) {
        console.log('[Hero] YouTube pause error:', err);
      }
      return;
    }

    // Vimeo video
    const vimeoPlayer = this.vimeoPlayers.get(wrapper);
    if (vimeoPlayer) {
      vimeoPlayer.pause().catch(err => {
        console.log('[Hero] Vimeo pause error:', err);
      });
      return;
    }
  }

  checkForYouTube() {
    return Array.from(this.videoWrappers).some(wrapper => {
      const iframe = wrapper.querySelector('iframe');
      return iframe && iframe.src.includes('youtube.com');
    });
  }

  checkForVimeo() {
    return Array.from(this.videoWrappers).some(wrapper => {
      const iframe = wrapper.querySelector('iframe');
      return iframe && iframe.src.includes('vimeo.com');
    });
  }

  loadYouTubeAPI() {
    if (window.YT && window.YT.Player) {
      this.youtubeAPIReady = true;
      this.setupYouTubePlayers();
      return;
    }

    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    // Save existing callback if any
    const existingCallback = window.onYouTubeIframeAPIReady;

    // Set up callback
    window.onYouTubeIframeAPIReady = () => {
      this.youtubeAPIReady = true;
      this.setupYouTubePlayers();
      
      // Call existing callback if it existed
      if (typeof existingCallback === 'function') {
        existingCallback();
      }
    };
  }

  loadVimeoAPI() {
    if (window.Vimeo && window.Vimeo.Player) {
      this.vimeoAPIReady = true;
      this.setupVimeoPlayers();
      return;
    }

    // Load Vimeo Player API
    const script = document.createElement('script');
    script.src = 'https://player.vimeo.com/api/player.js';
    script.onload = () => {
      this.vimeoAPIReady = true;
      this.setupVimeoPlayers();
    };
    document.head.appendChild(script);
  }

  setupYouTubePlayers() {
    this.videoWrappers.forEach(wrapper => {
      const iframe = wrapper.querySelector('iframe');
      if (!iframe || !iframe.src.includes('youtube.com')) return;

      // Add enablejsapi parameter to YouTube URL if not present
      if (!iframe.src.includes('enablejsapi')) {
        const separator = iframe.src.includes('?') ? '&' : '?';
        iframe.src += `${separator}enablejsapi=1`;
      }

      // Create YouTube player
      const player = new YT.Player(iframe, {
        events: {
          'onReady': () => {
            this.youtubePlayers.set(wrapper, player);
            // Ensure video is muted on load
            player.mute();
            // Handle visibility for this video
            this.handleVideoVisibility();
          },
          'onStateChange': (event) => {
            // Hide thumbnail when video starts playing
            if (event.data === YT.PlayerState.PLAYING) {
              this.hideThumbnail(wrapper);
            }
          }
        }
      });
    });
  }

  setupVimeoPlayers() {
    this.videoWrappers.forEach(wrapper => {
      const iframe = wrapper.querySelector('iframe');
      if (!iframe || !iframe.src.includes('vimeo.com')) return;

      const player = new Vimeo.Player(iframe);
      this.vimeoPlayers.set(wrapper, player);
      
      // Ensure video is muted on load
      player.setVolume(0);
      
      // Hide thumbnail when video starts playing
      player.on('play', () => {
        this.hideThumbnail(wrapper);
      });
      
      // Handle visibility for this video
      player.ready().then(() => {
        this.handleVideoVisibility();
      });
    });
  }

  setupNativeVideos() {
    this.videoWrappers.forEach(wrapper => {
      const video = wrapper.querySelector('video');
      if (video) {
        // Ensure video is muted on load
        video.muted = true;
        video.volume = 0;

        // Add loadeddata event to handle visibility once video is ready
        video.addEventListener('loadeddata', () => {
          this.handleVideoVisibility();
        }, { once: true });

        // Also try to load the video
        video.load();
      }
    });
  }

  setupAudioToggles() {
    this.videoWrappers.forEach(wrapper => {
      const toggleButton = wrapper.querySelector('[data-audio-toggle]');
      if (!toggleButton) return;

      const iconOff = toggleButton.querySelector('[data-icon-off]');
      const iconOn = toggleButton.querySelector('[data-icon-on]');
      let isMuted = true;

      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Check if this video wrapper is currently active in the viewport
        const videoType = wrapper.getAttribute('data-video-type');
        const heroSection = document.querySelector('.hero--has-mobile-media');
        const hasCustomMobileMedia = !!heroSection;
        
        let isCurrentlyActive = false;
        if (this.isDesktop()) {
          // Desktop: Only desktop videos are active
          isCurrentlyActive = (videoType === 'desktop');
        } else {
          // Mobile: Mobile videos if custom mobile media exists, otherwise desktop videos
          if (hasCustomMobileMedia) {
            isCurrentlyActive = (videoType === 'mobile');
          } else {
            isCurrentlyActive = (videoType === 'desktop');
          }
        }

        // Only toggle audio if this video is currently active in the viewport
        if (!isCurrentlyActive) {
          return;
        }

        // Check if any player exists before toggling
        const video = wrapper.querySelector('video');
        const hasYouTube = this.youtubePlayers.has(wrapper);
        const hasVimeo = this.vimeoPlayers.has(wrapper);
        
        if (!video && !hasYouTube && !hasVimeo) {
          // No player ready yet, ignore click
          return;
        }

        isMuted = !isMuted;

        // Update icons
        if (isMuted) {
          iconOff.style.display = 'block';
          iconOn.style.display = 'none';
        } else {
          iconOff.style.display = 'none';
          iconOn.style.display = 'block';
        }

        // Toggle audio for the appropriate video type
        this.toggleAudio(wrapper, isMuted);
      });
    });
  }

  toggleAudio(wrapper, isMuted) {
    // Check for native video
    const video = wrapper.querySelector('video');
    if (video) {
      video.muted = isMuted;
      video.volume = isMuted ? 0 : 1;
      return;
    }

    // Check for YouTube
    const youtubePlayer = this.youtubePlayers.get(wrapper);
    if (youtubePlayer) {
      if (isMuted) {
        youtubePlayer.mute();
      } else {
        youtubePlayer.unMute();
        youtubePlayer.setVolume(100);
      }
      return;
    }

    // Check for Vimeo
    const vimeoPlayer = this.vimeoPlayers.get(wrapper);
    if (vimeoPlayer) {
      vimeoPlayer.setVolume(isMuted ? 0 : 1);
      return;
    }
  }

  hideThumbnail(wrapper) {
    // Add class to wrapper to hide thumbnail via CSS
    wrapper.classList.add('video-loaded');
    
    // Also directly hide the thumbnail element if it exists
    const thumbnail = wrapper.querySelector('[data-video-thumbnail]');
    if (thumbnail) {
      thumbnail.style.opacity = '0';
      thumbnail.style.pointerEvents = 'none';
    }
  }
}

// Initialize when script loads
new HeroAudioControl();
